import { FunctionComponent, memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  OrganisationTag,
  OrganisationTagType,
  StoryCardTeaserImageStyle,
} from '../../../../../services';
import { useCardFilter, useTags } from '../../../../hooks';
import { isAdminLayout, optArrItem } from '../../../../utils';
import { Filter, FilterValue } from '../../../Filter';
import { TabItem, Tabs } from '../../../Tabs';
import { TagsPicker } from '../../../TagsPicker';
import { TeaserStyle } from './TeaserStyle';

import classes from './CreateCardSettings.module.scss';

export enum CreateCardSettingsTabType {
  CARD,
  TAGS,
  MEDIA,
}

interface CreateCardSettingsProps {
  activeSettingsTabIndex: number;
  setActiveSettingsTabIndex: (index: number) => void;
  externalTags: OrganisationTag[];
  setExternalTags: (tags: OrganisationTag[]) => void;
  externalStatusTags: OrganisationTag[];
  setExternalStatusTags: (tags: OrganisationTag[]) => void;
  internalTags: OrganisationTag[];
  setInternalTags: (tags: OrganisationTag[]) => void;
  cardFilter: FilterValue[];
  setCardFilter: (filter: FilterValue[]) => void;
  showEnableComments: boolean;
  showDisplayUser: boolean;
  onTabChangeCallback: () => void;
  showMedia?: boolean;
  teaserStyle?: StoryCardTeaserImageStyle;
  setTeaserStyle?: (teaserStyle: StoryCardTeaserImageStyle) => void;
}

export const CreateCardSettings: FunctionComponent<CreateCardSettingsProps> = memo(
  ({
    activeSettingsTabIndex,
    setActiveSettingsTabIndex,
    externalTags,
    setExternalTags,
    externalStatusTags,
    setExternalStatusTags,
    internalTags,
    setInternalTags,
    cardFilter,
    setCardFilter,
    showEnableComments,
    showDisplayUser,
    onTabChangeCallback,
    showMedia,
    teaserStyle,
    setTeaserStyle,
  }) => {
    const { t } = useTranslation();

    const { hasInternalTags, hasExternalTags, hasExternalStatusTags, hasContentTags } = useTags();

    const { groups: cardGroups } = useCardFilter({
      filter: cardFilter,
      showEnableComments,
      showDisplayUser,
      showEnableMentions: false,
    });

    const onTabChange = useCallback(
      (index: number) => {
        setActiveSettingsTabIndex(index);
        onTabChangeCallback();
      },
      [onTabChangeCallback, setActiveSettingsTabIndex]
    );

    const teaserStyleContent = useMemo(() => {
      if (!teaserStyle || !setTeaserStyle) {
        return null;
      }

      return (
        <div className={classes['create-card-settings__media']}>
          <TeaserStyle teaserStyle={teaserStyle} setTeaserStyle={setTeaserStyle} />
        </div>
      );
    }, [setTeaserStyle, teaserStyle]);

    const tabItems: TabItem[] = useMemo(
      () => [
        ...optArrItem(isAdminLayout(), {
          index: CreateCardSettingsTabType.CARD,
          name: t('createCardSettingsTabs.card'),
          content: <Filter filter={cardFilter} groups={cardGroups} onChange={setCardFilter} />,
        }),
        ...optArrItem(hasContentTags, {
          index: CreateCardSettingsTabType.TAGS,
          name: t('createCardSettingsTabs.tags'),
          content: (
            <div className={classes['create-card-settings__tags']}>
              {hasInternalTags && (
                <TagsPicker
                  tags={internalTags}
                  setTags={setInternalTags}
                  type={OrganisationTagType.INTERNAL}
                />
              )}
              {hasExternalTags && (
                <TagsPicker
                  tags={externalTags}
                  setTags={setExternalTags}
                  type={OrganisationTagType.EXTERNAL}
                  hideTitle={!isAdminLayout()}
                  hideSubTitle={!isAdminLayout()}
                />
              )}
              {hasExternalStatusTags && (
                <TagsPicker
                  tags={externalStatusTags}
                  setTags={setExternalStatusTags}
                  type={OrganisationTagType.EXTERNAL_STATUS}
                  hideTitle={!isAdminLayout()}
                  hideSubTitle={!isAdminLayout()}
                  singleSelect
                  closeOnSelect
                />
              )}
            </div>
          ),
        }),
        ...optArrItem(Boolean(showMedia && teaserStyleContent), {
          index: CreateCardSettingsTabType.MEDIA,
          name: t('createCardSettingsTabs.media'),
          content: teaserStyleContent,
        }),
      ],
      [
        cardFilter,
        cardGroups,
        externalTags,
        hasExternalTags,
        hasInternalTags,
        hasExternalStatusTags,
        hasContentTags,
        internalTags,
        setCardFilter,
        setExternalTags,
        setInternalTags,
        setExternalStatusTags,
        externalStatusTags,
        showMedia,
        t,
        teaserStyleContent,
      ]
    );

    return (
      <div className={classes['create-card-settings']}>
        <Tabs
          items={tabItems}
          selectedTabIndex={activeSettingsTabIndex}
          setSelectedTabIndex={onTabChange}
          noBackground
        />
      </div>
    );
  }
);
